import React from 'react';
import { getInitials, timeAgo, getDisplayContact } from '../../utils/messageHelpers';
import { getCSWStatus } from '../../utils/conversationHelpers';
import { Conversation } from '../../types/wa_messages';

interface ConversationItemProps {
  conversation: Conversation;
  isActive: boolean;
  onClick: (conversationId: string) => void;
}

const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  isActive,
  onClick,
}) => {
  const handleClick = () => {
    onClick(conversation.id);
  };

  return (
    <div
      className={`flex items-start gap-4 px-6 py-3 cursor-pointer transition w-full box-border overflow-hidden ${isActive ? 'bg-[#eaf1fb] shadow-sm ring-2 ring-blue-200' : 'hover:bg-slate-50'}`}
      onClick={handleClick}
    >
      {/* Avatar */}
      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xl select-none">
        {getInitials(conversation.pic_name || '', getDisplayContact(conversation.pic_contact))}
      </div>
      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 min-w-0">
            <span className="text-base text-slate-900 truncate">{conversation.pic_name || ''}</span>
            {conversation.xero_direct_contact_name && conversation.xero_direct_contact_id && (
              <a
                href={`/organizations/${conversation.organization_id}/customers/${conversation.xero_direct_contact_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate"
                onClick={(e) => e.stopPropagation()}
              >
                ({conversation.xero_direct_contact_name})
              </a>
            )}
          </div>
        </div>
        {/* Contact type and organization */}
        <div className="flex items-center gap-1 min-w-0">
          <span className="text-xs font-medium text-slate-600">
            {conversation.user_id ? 'Internal' : 'Customer'}
          </span>
          {conversation.organization_name && (
            <a
              href={`/organizations/${conversation.organization_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline truncate"
              onClick={(e) => e.stopPropagation()}
            >
              @{conversation.organization_name}
            </a>
          )}
        </div>
        {/* Remove raw pic_contact string; per-message chips will show contacts */}
        {/* CSW status hint */}
        {(() => {
          const { closed, closeTime } = getCSWStatus(conversation);
          if (closed && !closeTime) {
            // CSW closed without timestamp (no latest WhatsApp inbound message)
            return (
              <div className="flex items-center gap-1 mt-1">
                <span className="text-xs text-rose-400 italic font-medium">
                  WA-CSW closed.
                </span>
              </div>
            );
          }
          if (!closeTime) return null;
          return (
            <div className="flex items-center gap-1 mt-1">
              {closed ? (
                <>
                  <span className="text-xs text-rose-400 italic font-medium">
                    WA-CSW closed: {closeTime.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-xs text-blue-400 italic font-medium">
                    WA-CSW will close at : {closeTime.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                  </span>
                </>
              )}
            </div>
          );
        })()}
        {/* Timestamp at bottom */}
        <div className="text-xs text-slate-400 whitespace-nowrap">{timeAgo(conversation.last_activity)}</div>
      </div>
    </div>
  );
};

export default ConversationItem; 